// src/components/CoverDesigner/CoverDesigner.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canSaveStory } from '../../utils/authChecks';
import SignupPrompt from '../Account/SignupPrompt';
import Notification from '../Account/Notification';
import { StoryService } from '../../services/storyService';
import { CurvedText } from '../Storybook/CoverRenderer';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
interface CoverElement {
  id: string;
  type: 'sticker' | 'text' | 'icon' | 'graphic';
  content: string;
  x: number;
  y: number;
  fontSize?: number;
  fontFamily?: string;
  rotation?: number;
  scale?: number;
  color?: string;
  iconName?: string;
  imageUrl?: string;
  textStyle?: 'shadow' | 'outline' | 'glow' | 'none';
  textCurve?: 'none' | 'up' | 'down'; // NEW
}
 
interface ImageBackground { name: string; url: string; thumbnail?: string; }
interface Graphic { id: string; name: string; url: string; }
interface GraphicsLibrary {
    places: Graphic[]; people: Graphic[]; animals: Graphic[];
    'speech bubbles': Graphic[]; objects: Graphic[]; custom: Graphic[];
}
 
interface CoverDesignerProps {
    storyTitle: string;
    authorName: string;
    onSave: (coverData: any) => void;
    onClose?: () => void;
    onManualSave?: () => void;
    initialCoverData?: any;
    onShowSignupPrompt?: () => void;
    storyId?: string | null;
    currentUser?: any;
}
 
// ── Lazy image ────────────────────────────────────────────────────────────────
const LazyGraphicImage: React.FC<{ src: string; alt: string; style?: React.CSSProperties; onLoad?: () => void }> = ({ src, alt, style, onLoad }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);
 
    useEffect(() => {
        if (!imgRef.current) return;
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.disconnect(); } }, { rootMargin: '100px' });
        observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, []);
 
    return (
        <img ref={imgRef} src={isInView ? src : undefined} alt={alt}
            style={{ ...style, opacity: isLoaded ? 1 : 0, transition: 'opacity 0.2s ease' }}
            onLoad={() => { setIsLoaded(true); onLoad?.(); }} />
    );
};
 
// ── Graphic button ────────────────────────────────────────────────────────────
const GraphicButton = React.memo<{ graphic: Graphic; onClick: (g: Graphic) => void; onDelete?: (id: string) => void }>(({ graphic, onClick, onDelete }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div style={{ position: 'relative', minHeight: '70px' }}>
            <button onClick={() => onClick(graphic)}
                style={{ width: '100%', height: '100%', minHeight: '70px', padding: '8px', background: C.surfaceLowest, border: `1.5px solid ${hovered ? C.primary : C.outlineVariant}`, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', transform: hovered ? 'scale(1.05)' : 'scale(1)' }}
                onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                <LazyGraphicImage src={graphic.url} alt={graphic.name} style={{ maxWidth: '100%', maxHeight: '50px', objectFit: 'contain' }} />
                <span style={{ fontSize: '10px', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{graphic.name}</span>
            </button>
            {onDelete && (
                <button onClick={() => onDelete(graphic.id)}
                    style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', background: C.error, color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    ×
                </button>
            )}
        </div>
    );
});
GraphicButton.displayName = 'GraphicButton';

const getTextShadow = (style: string | undefined, color: string | undefined): string => {
  switch (style) {
      case 'outline': return `-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000`;
      case 'glow':    return `0 0 8px ${color || '#fff'}, 0 0 20px ${color || '#fff'}, 0 0 35px ${color || '#fff'}`;
      case 'none':    return 'none';
      default:        return '3px 3px 6px rgba(0,0,0,0.4)'; // 'shadow'
  }
};
 
// ── Canvas element ────────────────────────────────────────────────────────────
const CanvasElement = React.memo<{
    element: CoverElement; isSelected: boolean; isDragging: boolean;
    onInteractionStart: (id: string, e: React.MouseEvent | React.TouchEvent) => void;
    onUpdate: (id: string, updates: Partial<CoverElement>) => void;
    onDelete: (id: string) => void; onSelect: (id: string) => void;
    setActiveTab: (tab: 'background' | 'stickers' | 'my-art' | 'text') => void;
}>(({ element, isSelected, isDragging, onInteractionStart, onUpdate, onDelete, onSelect, setActiveTab }) => (
    <div
        key={`${element.id}-${element.fontSize}-${element.fontFamily}-${element.color}`}
        onMouseDown={e => onInteractionStart(element.id, e)}
        onTouchStart={e => onInteractionStart(element.id, e)}
        style={{
            position: 'absolute', left: `${element.x}%`, top: `${element.y}%`,
            transform: `translate(-50%,-50%) rotate(${element.type === 'icon' || element.type === 'graphic' ? element.rotation || 0 : 0}deg) scale(${element.type === 'icon' || element.type === 'graphic' ? element.scale || 1 : 1})`,
            cursor: 'grab', userSelect: 'none', WebkitUserSelect: 'none', padding: '6px', whiteSpace: 'nowrap', touchAction: 'none',
            transition: isDragging ? 'none' : 'left 0.06s ease-out, top 0.06s ease-out',
        }}
    >
        {element.type === 'graphic' && element.imageUrl ? (
            <LazyGraphicImage src={element.imageUrl} alt={element.content}
                style={{ maxWidth: '200px', maxHeight: '200px', width: 'auto', height: 'auto', display: 'block', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))', pointerEvents: 'none' }} />
        ) : element.type === 'icon' ? (
            (() => {
                const icons: any = {
                    'heart':'♥','star':'★','sparkles':'✨','circle':'○','square':'□','triangle':'△',
                    'crown':'👑','wand':'🪄','castle':'🏰','rocket':'🚀','zap':'⚡','shield':'🛡️',
                    'cloud':'☁️','sun':'☀️','moon':'🌙','tree':'🌲','flower':'🌸','rainbow':'🌈',
                    'gift':'🎁','music':'🎵','camera':'📷','book':'📖','palette':'🎨','pencil':'✏️',
                    'award':'🏆','flag':'🚩','target':'🎯'
                };
                return (
                    <span style={{ fontSize: `${element.fontSize || 48}px`, color: element.color || '#6C5CE7', pointerEvents: 'none', display: 'block' }}>
                        {icons[element.iconName || ''] || '✦'}
                    </span>
                );
            })()

          ) : element.type === 'text' ? (
            element.textCurve && element.textCurve !== 'none' ? (
                // Curved
                <div onClick={e => { e.stopPropagation(); onSelect(element.id); }}
                    style={{ outline: isSelected ? `2px dashed ${C.primary}` : 'none', outlineOffset: '4px', borderRadius: '4px', cursor: isSelected ? 'default' : 'move' }}>
                    <CurvedText
    content={element.content}
    curve={element.textCurve}
    fontSize={element.fontSize ?? 24}
    fontFamily={element.fontFamily ?? 'Inter'}
    color={element.color ?? '#FFFFFF'}
    textStyle={element.textStyle}  // ← was textShadow={getTextShadow(...)}
/>
                </div>
            ) : (
                // Straight
                <div
                    contentEditable={isSelected} suppressContentEditableWarning
                    onBlur={e => { const t = e.currentTarget.textContent || ''; if (t.trim()) onUpdate(element.id, { content: t }); }}
                    onKeyDown={e => { if (isSelected) e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                    onMouseDown={e => { if (isSelected && e.detail === 2) e.stopPropagation(); }}
                    onClick={e => { e.stopPropagation(); if (!isSelected) onSelect(element.id); }}
                    onDoubleClick={e => { e.stopPropagation(); onSelect(element.id); const s = window.getSelection(); const r = document.createRange(); r.selectNodeContents(e.currentTarget); s?.removeAllRanges(); s?.addRange(r); }}
                    style={{ outline: isSelected ? `2px dashed ${C.primary}` : 'none', outlineOffset: '4px', minWidth: '20px', cursor: isSelected ? 'text' : 'move', pointerEvents: 'auto', background: isSelected ? 'rgba(255,255,255,0.1)' : 'transparent', borderRadius: '4px', padding: isSelected ? '4px' : '0', fontSize: `${element.fontSize}px`, fontFamily: element.fontFamily || 'Inter', fontWeight: '800', color: element.color || '#FFFFFF', textShadow: getTextShadow(element.textStyle, element.color) }}
                >{element.content}</div>
            )  // ← closes the inner curve ternary
        ) : element.content} 
 
        {isSelected && (
            <>
                <div style={{ position: 'absolute', top: '-10px', left: '-10px', right: '-10px', bottom: '-10px', border: `2px solid ${C.primary}`, borderRadius: '8px', pointerEvents: 'none' }} />
                {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map(corner => (
                    <div key={corner} style={{ position: 'absolute', width: '10px', height: '10px', background: 'white', border: `2px solid ${C.primary}`, borderRadius: '50%', ...(corner === 'top-left' && { top: '-15px', left: '-15px' }), ...(corner === 'top-right' && { top: '-15px', right: '-15px' }), ...(corner === 'bottom-left' && { bottom: '-15px', left: '-15px' }), ...(corner === 'bottom-right' && { bottom: '-15px', right: '-15px' }), pointerEvents: 'none' }} />
                ))}
                {/* Floating toolbar */}
                <div
                    onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '10px', background: C.surfaceLowest, borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '6px 8px', display: 'flex', gap: '2px', alignItems: 'center', pointerEvents: 'auto', zIndex: 1000, whiteSpace: 'nowrap', border: `1px solid ${C.surfaceHigh}`, scale: `${Math.min(1, 1 / (element.scale || 1))}` }}
                >
                    <ToolbarBtn icon="🗑️" title="Delete"  onClick={() => onDelete(element.id)} danger />
                    {(element.type === 'icon' || element.type === 'graphic') && (
                        <>
                            <ToolbarBtn icon="−" title="Smaller" onClick={() => onUpdate(element.id, { scale: Math.max(0.5, (element.scale || 1) - 0.2) })} />
                            <ToolbarBtn icon="+" title="Bigger"  onClick={() => onUpdate(element.id, { scale: Math.min(3, (element.scale || 1) + 0.2) })} />
                            <ToolbarBtn icon="🔄" title="Rotate" onClick={() => onUpdate(element.id, { rotation: ((element.rotation || 0) + 45) % 360 })} />
                        </>
                    )}
                    {element.type === 'text' && (
                        <>
                            <ToolbarBtn icon="−" title="Smaller"   onClick={() => onUpdate(element.id, { fontSize: Math.max(12, (element.fontSize || 24) - 4) })} />
                            <ToolbarBtn icon="+" title="Bigger"    onClick={() => onUpdate(element.id, { fontSize: Math.min(120, (element.fontSize || 24) + 4) })} />
                            <ToolbarBtn icon="✏️" title="Edit Text" onClick={() => setActiveTab('text')} />
                        </>
                    )}
                </div>
            </>
        )}
    </div>
));
CanvasElement.displayName = 'CanvasElement';
 
// Small toolbar button used inside the canvas floating toolbar
const ToolbarBtn: React.FC<{ icon: string; title: string; onClick: () => void; danger?: boolean }> = ({ icon, title, onClick, danger }) => (
    <button onClick={e => { e.stopPropagation(); onClick(); }} title={title}
        style={{ padding: '6px 10px', minWidth: '36px', minHeight: '36px', background: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', color: danger ? C.error : C.onSurface, transition: 'background 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.background = danger ? '#fef2f2' : C.surfaceLow; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
        {icon}
    </button>
);
 
// ── Section toggle used in side panel ─────────────────────────────────────────
const SectionHeader: React.FC<{ label: string; count?: number; open: boolean; onToggle: () => void }> = ({ label, count, open, onToggle }) => (
    <button onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: open ? C.primaryContainer : C.surfaceLow, border: `1.5px solid ${open ? C.primaryLight : C.outlineVariant}`, borderRadius: '10px', cursor: 'pointer', marginBottom: open ? '12px' : '0', transition: 'all 0.15s' }}>
        <span style={{ fontSize: '0.72rem', color: open ? C.primary : C.onSurfaceVariant }}>{open ? '▼' : '▶'}</span>
        <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: '700', color: open ? C.primary : C.onBackground, fontFamily: "'Epilogue', sans-serif", textTransform: 'capitalize', textAlign: 'left' }}>{label}</span>
        {count !== undefined && <span style={{ background: open ? C.primary : C.surfaceHigh, color: open ? '#fff' : C.onSurfaceVariant, padding: '1px 8px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800' }}>{count}</span>}
    </button>
);

// ── Image compression (Canvas API) ───────────────────────────────────────────
async function compressImage(
  file: File,
  opts: { maxDim?: number; quality?: number } = {}
): Promise<string> {
  const { maxDim = 2000, quality = 0.82 } = opts;
  return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
          URL.revokeObjectURL(url);
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
              const ratio = Math.min(maxDim / width, maxDim / height);
              width  = Math.round(width  * ratio);
              height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width  = width;
          canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
      img.src = url;
  });
}
 
// ── Main component ─────────────────────────────────────────────────────────────
const CoverDesigner: React.FC<CoverDesignerProps> = ({ storyTitle, authorName, onSave, onClose, onManualSave, initialCoverData, onShowSignupPrompt, storyId, currentUser }) => {
 
    const [showSignupPrompt, setShowSignupPrompt]     = useState(false);
    const [activeTab, setActiveTab]                   = useState< 'background' | 'stickers' | 'my-art' | 'text'>('background');
    const [background, setBackground]                 = useState(initialCoverData?.background || { type: 'solid', value: '#FFFFFF' });
    const [elements, setElements]                     = useState<CoverElement[]>(
        initialCoverData?.elements || [
            { id: 'title',  type: 'text', content: storyTitle,        x: 50, y: 30, fontSize: 32, fontFamily: 'Fredoka',   color: '#000000' },
            { id: 'author', type: 'text', content: `by ${authorName}`, x: 50, y: 85, fontSize: 16, fontFamily: 'Inter',     color: '#000000' },
        ]
    );
    const [selectedElement, setSelectedElement]       = useState<string | null>(null);
    const [dragging, setDragging]                     = useState<string | null>(null);
    const [expandedSections, setExpandedSections]     = useState<Set<string>>(new Set(['places']));
    const [showBgImages, setShowBgImages]             = useState(true);
    const [showSolidColors, setShowSolidColors]       = useState(true);
    const [showGradients, setShowGradients]           = useState(false);
    const [customGraphics, setCustomGraphics]         = useState<Graphic[]>([]);
    const [uploadingGraphic, setUploadingGraphic]     = useState(false);
    const [isMobile, setIsMobile]                     = useState(window.innerWidth < 640);
    const [isTablet, setIsTablet]                     = useState(window.innerWidth >= 640 && window.innerWidth < 1024);
   const [notification, setNotification]             = useState<{ show: boolean; type: 'image-too-large' | 'error' }>({ show: false, type: 'error' });
   const [canvasWidth, setCanvasWidth]               = useState(500);

   // ── History (undo/redo) ───────────────────────────────────────────────────────
const elementsRef    = useRef(elements);
const backgroundRef  = useRef(background);
useEffect(() => { elementsRef.current   = elements;   }, [elements]);
useEffect(() => { backgroundRef.current = background; }, [background]);

const historyRef      = useRef<Array<{ elements: CoverElement[]; background: any }>>([
    { elements, background }
]);
const historyIndexRef = useRef(0);
const [canUndo, setCanUndo] = useState(false);
const [canRedo, setCanRedo] = useState(false);

// ── Snap guides ───────────────────────────────────────────────────────────────
const [snapLines, setSnapLines] = useState<{ x?: number; y?: number }>({});
const SNAP_POINTS  = [33.33, 50, 66.66];
const SNAP_THRESH  = 4;

   const canvasRoRef                                 = useRef<ResizeObserver | null>(null);
   const canvasOuterRef                              = useCallback((node: HTMLDivElement | null) => {
       if (canvasRoRef.current) { canvasRoRef.current.disconnect(); canvasRoRef.current = null; }
       if (!node) return;
       const ro = new ResizeObserver(([entry]) => {
           if (entry.contentRect.width > 0) setCanvasWidth(entry.contentRect.width);
       });
       ro.observe(node);
       canvasRoRef.current = ro;
   }, []);
 
    useEffect(() => {
        const h = () => {
            const w = window.innerWidth;
            setIsMobile(w < 640);
            setIsTablet(w >= 640 && w < 1024);
        };
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);


 
    // ── Memoized constants ────────────────────────────────────────────────────
    const solidColors = useMemo(() => ['#FFFFFF','#4ECDC4','#FF6B6B','#95E1D3','#ff9f00','#AA96DA','#FCBAD3','#2a52be','#A8D8EA','#6C5CE7','#FD79A8','#00B894','#FDCB6E','#1b1b1b','#dcdcdc'], []);
    const gradients   = useMemo(() => ['linear-gradient(135deg,#667eea 0%,#764ba2 100%)','linear-gradient(135deg,#f093fb 0%,#f5576c 100%)','linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)','linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)','linear-gradient(135deg,#fa709a 0%,#fee140 100%)','linear-gradient(135deg,#30cfd0 0%,#330867 100%)'], []);
    const patterns    = useMemo(() => [{ name: 'Stars', value: 'radial-gradient(circle,#FFD700 1px,transparent 1px)', size: '20px 20px' },{ name: 'Dots', value: 'radial-gradient(circle,rgba(255,255,255,0.3) 2px,transparent 2px)', size: '15px 15px' },{ name: 'Stripes', value: 'repeating-linear-gradient(45deg,transparent,transparent 10px,rgba(255,255,255,0.1) 10px,rgba(255,255,255,0.1) 20px)' }], []);
 
    const imageBackgrounds: ImageBackground[] = useMemo(() => [
        { name: 'Lined Paper',     url: '/images/backgrounds/lined_paper.png' },
        { name: 'Sunny Day',       url: '/images/backgrounds/sunny_day.png' },
        { name: 'Midnight Library',url: '/images/backgrounds/midnight_library.jpg' },
        { name: 'Forest',          url: '/images/backgrounds/dark_forest.png' },
        { name: 'Flower Field',    url: '/images/backgrounds/flower_fields.jpg' },
        { name: 'Planet',          url: '/images/backgrounds/planet.png' },
        { name: 'Snow',            url: '/images/backgrounds/snow.png' },
        { name: 'City',            url: '/images/backgrounds/city.png' },
        { name: 'Fields',          url: '/images/backgrounds/fields.jpg' },
        { name: 'Orange Sky',      url: '/images/backgrounds/orange_sky.jpg' },
        { name: 'Starry Night',    url: '/images/backgrounds/starry_night.png' },
        { name: 'Crater',          url: '/images/backgrounds/crater.png' },
        { name: 'Underwater',      url: '/images/backgrounds/underwater.png' },
        { name: 'Camp',            url: '/images/backgrounds/camp.png' },
        { name: 'Space Sky',       url: '/images/backgrounds/pink_space.png' },
        { name: 'Green Forest',    url: '/images/backgrounds/green_forest.png' },
        { name: 'Spaceman',        url: '/images/backgrounds/spaceman.png' },
        { name: 'Spaceman',        url: '/images/backgrounds/cotton_candy_sky.png' },
    ], []);
 
    const graphicsLibrary: GraphicsLibrary = useMemo(() => ({
        places:          [{ id: 'school', name: 'School', url: '/images/graphics/school.png' },{ id: 'soccer field', name: 'Soccer Field', url: '/images/graphics/soccer_field.png' },{ id: 'campfire', name: 'Campfire', url: '/images/graphics/campfire.png' }],
        people:          [{ id: 'boy at desk', name: 'Boy at Desk', url: '/images/graphics/school_boy_at_desk.png' },{ id: 'manga boy', name: 'Manga Boy', url: '/images/graphics/manga_boy.png' },{ id: 'girl books', name: 'Girl w/ Books', url: '/images/graphics/girl_books.png' },{ id: 'anime girl', name: 'Anime Girl', url: '/images/graphics/anime_girl.png' }],
        animals:         [{ id: 'raccoon', name: 'Raccoon', url: '/images/graphics/raccoon.png' },{ id: 'llamas', name: 'Llamas', url: '/images/graphics/llamas.jpg' },{ id: 'paw print', name: 'Paw Print', url: '/images/graphics/paw_print.jpg' },{ id: 'drawon', name: 'Dragon', url: '/images/graphics/dragon.png' },{ id: 'german_boxer', name: 'Boxer Dog', url: '/images/graphics/german_boxer.png' },{ id: 'panda', name: 'Panda', url: '/images/graphics/panda.png' }],
        'speech bubbles':[{ id: 'comic bubble 1', name: 'Comic Bubble', url: '/images/graphics/comic_bubble_1.png' },{ id: 'comic bubble 12', name: 'Bubble Left', url: '/images/graphics/comic_bubble_2_left.png' }],
        objects:         [{ id: 'robot', name: 'Robot', url: '/images/graphics/robot.jpg' },{ id: 'backpack', name: 'Backpack', url: '/images/graphics/backpack.png' },{ id: 'paper plane', name: 'Paper Plane', url: '/images/graphics/paper_plane1.png' },{ id: 'school supplies', name: 'Supplies', url: '/images/graphics/school_supplies.png' },{ id: 'school bus', name: 'School Bus', url: '/images/graphics/school_bus.png' },{ id: 'back to school', name: 'Back to School', url: '/images/graphics/back_to_school.png' }],
        custom: customGraphics,
    }), [customGraphics]);
 
    const fonts = useMemo(() => [
        { name: 'Fredoka',    value: 'Fredoka' },
        { name: 'Comic',      value: '"Comic Sans MS","Comic Sans",cursive' },
        { name: 'Bubbly',     value: 'Quicksand' },
        { name: 'Adventure',  value: '"Kalam",cursive' },
        { name: 'Modern',     value: 'Inter' },
        { name: 'Classic',    value: 'Georgia,serif' },
        { name: 'Bold',       value: '"Arial Black","Arial Bold",sans-serif' },
        { name: 'Typewriter', value: '"Courier New",monospace' },
    ], []);
 
    const textColors = useMemo(() => [
        { name: 'White',   value: '#FFFFFF' }, { name: 'Black',    value: '#000000' },
        { name: 'Red',     value: '#dc143c' }, { name: 'Teal',     value: '#4ECDC4' },
        { name: 'Navy',    value: '#000080' }, { name: 'Purple',   value: '#AA96DA' },
        { name: 'Green',   value: '#00B894' }, { name: 'Pink',     value: '#FD79A8' },
        { name: 'Grey',    value: '#dcdcdc' }, { name: 'Gold',     value: '#ffd700' },
    ], []);
 
    const tabConfig = useMemo(() => [
      { id: 'background', label: 'Background', icon: '🎨' },
      { id: 'stickers',   label: 'Stickers',   icon: '✨' },
      { id: 'my-art',     label: 'My Art',     icon: '🖼️' },
      { id: 'text',       label: 'Text',       icon: '📝' },
  ], []);


 
    // ── Auto-save ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!storyId || !currentUser?.uid) return;
        const timer = setTimeout(() => {
            const freshCoverData = { background, elements };
            onSave(freshCoverData);
            StoryService.updateStory(currentUser.uid || '', storyId, { thumbnail: JSON.stringify(freshCoverData) }).catch(console.error);
        }, 1000);
        return () => clearTimeout(timer);
    // NEW
}, [elements, background, storyId, currentUser?.uid]); // onSave intentionally omitted — it's called inside via closure

// ── History helpers ───────────────────────────────────────────────────────────
const syncHistoryButtons = useCallback(() => {
  setCanUndo(historyIndexRef.current > 0);
  setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
}, []);

const pushHistory = useCallback(() => {
  historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
  historyRef.current.push({ elements: elementsRef.current, background: backgroundRef.current });
  if (historyRef.current.length > 60) historyRef.current.shift();
  historyIndexRef.current = historyRef.current.length - 1;
  syncHistoryButtons();
}, [syncHistoryButtons]);

const undo = useCallback(() => {
  if (historyIndexRef.current <= 0) return;
  historyIndexRef.current--;
  const snap = historyRef.current[historyIndexRef.current];
  setElements(snap.elements);
  setBackground(snap.background);
  setSelectedElement(null);
  syncHistoryButtons();
}, [syncHistoryButtons]);

const redo = useCallback(() => {
  if (historyIndexRef.current >= historyRef.current.length - 1) return;
  historyIndexRef.current++;
  const snap = historyRef.current[historyIndexRef.current];
  setElements(snap.elements);
  setBackground(snap.background);
  setSelectedElement(null);
  syncHistoryButtons();
}, [syncHistoryButtons]);

// Ctrl/Cmd+Z / Ctrl/Cmd+Y / Ctrl/Cmd+Shift+Z
useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.key === 'z' &&  e.shiftKey) || e.key === 'y') { e.preventDefault(); redo(); }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [undo, redo]);

// Wrap setBackground so every background change is one history entry
const handleSetBackground = useCallback((bg: any) => {
  backgroundRef.current = bg;
  setBackground(bg);
  pushHistory();
}, [pushHistory]);
 
    // ── Memoized callbacks ────────────────────────────────────────────────────
    const toggleSection = useCallback((key: string) => {
        setExpandedSections(prev => {
            const arr = Array.from(prev);
            return new Set(arr.includes(key) ? arr.filter(c => c !== key) : [...arr, key]);
        });
    }, []);
 
    // Sticker upload — compress, add as canvas element
const handleGraphicUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  e.target.value = '';                              // allow re-upload of same file
  if (!file) return;
  if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
  if (file.size > 15 * 1024 * 1024) { setNotification({ show: true, type: 'image-too-large' }); return; } // 15 MB gate
  setUploadingGraphic(true);
  try {
      const dataUrl = await compressImage(file, { maxDim: 800, quality: 0.82 });
      const g: Graphic = { id: `custom-${Date.now()}`, name: file.name.replace(/\.[^/.]+$/, ''), url: dataUrl };
      setCustomGraphics(p => [...p, g]);
      setExpandedSections(p => new Set([...Array.from(p), 'custom']));
  } catch { alert('Error uploading image'); }
  finally { setUploadingGraphic(false); }
}, []);

// Full-cover upload — compress to fill the entire background
const [uploadingCover, setUploadingCover] = useState(false);
const handleCoverUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
    if (file.size > 50 * 1024 * 1024) { alert('File too large (max 50 MB)'); return; }
    setUploadingCover(true);
    try {
        // Keep enough resolution for a 5×8 print at 150 dpi → 750×1200px
        const dataUrl = await compressImage(file, { maxDim: 1500, quality: 0.88 });
        setBackground({ type: 'image', value: dataUrl });
    } catch { alert('Error processing image'); }
    finally { setUploadingCover(false); }
}, []);
 
    const deleteCustomGraphic = useCallback((id: string) => {
        setCustomGraphics(p => p.filter(g => g.id !== id));
        setElements(p => p.filter(el => el.id !== id));
    }, []);
 
    const handleInteractionStart = useCallback((id: string, e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault(); e.stopPropagation();
        if ('touches' in e) navigator.vibrate?.(8);
        setDragging(id); setSelectedElement(id);
    }, []);
 
    // handleInteractionMove — adds snap-to-thirds + guide lines
const handleInteractionMove = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
  if (!dragging) return;
  const rect = e.currentTarget.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
  let x = ((clientX - rect.left) / rect.width)  * 100;
  let y = ((clientY - rect.top)  / rect.height) * 100;

  let snapX: number | undefined, snapY: number | undefined;
  for (const sp of SNAP_POINTS) {
      if (Math.abs(x - sp) < SNAP_THRESH) { x = sp; snapX = sp; }
      if (Math.abs(y - sp) < SNAP_THRESH) { y = sp; snapY = sp; }
  }
  setSnapLines({ x: snapX, y: snapY });

  setElements(prev => prev.map(el =>
      el.id === dragging ? { ...el, x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) } : el
  ));
}, [dragging, SNAP_POINTS, SNAP_THRESH]);
 
    // handleInteractionEnd — clears guides, commits drag to history
const handleInteractionEnd = useCallback(() => {
  if (dragging) { setSnapLines({}); pushHistory(); }
  setDragging(null);
}, [dragging, pushHistory]);

    const handleCanvasClick    = useCallback((e: React.MouseEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) setSelectedElement(null); }, []);

    const addGraphicToCanvas = useCallback((graphic: Graphic) => {
      const el: CoverElement = {
          id: `graphic-${Date.now()}`, type: 'graphic', content: graphic.name,
          imageUrl: graphic.url, x: 50, y: 50, rotation: 0, scale: 1,
      };
      setElements(prev => {
          const next = [...prev, el];
          elementsRef.current = next;
          return next;
      });
      setSelectedElement(el.id);
      setTimeout(pushHistory, 0);
  }, [pushHistory]);

// updateElement — pass addToHistory=true for discrete changes (color, font, style)
const updateElement = useCallback((id: string, updates: Partial<CoverElement>, addToHistory = false) => {
  setElements(prev => {
      const next = prev.map(el => el.id === id ? { ...el, ...updates } : el);
      elementsRef.current = next;
      if (addToHistory) pushHistory();
      return next;
  });
}, [pushHistory]);

const deleteElement = useCallback((id: string) => {
  setElements(prev => {
      const next = prev.filter(el => el.id !== id);
      elementsRef.current = next;
      return next;
  });
  setSelectedElement(null);
  setTimeout(pushHistory, 0);
}, [pushHistory]);
 
    const getBackgroundStyle = useCallback(() => {
        if (background.type === 'solid')    return { background: background.value };
        if (background.type === 'gradient') return { background: background.value };
        if (background.type === 'image')    return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' };
        if (background.type === 'pattern') {
            const p = patterns.find(p => p.name === background.value);
            return { background: '#6C5CE7', backgroundImage: p?.value, backgroundSize: p?.size };
        }
        return { background: '#6C5CE7' };
    }, [background, patterns]);
 
    const handleClose = useCallback(() => {
        onSave({ background, elements });
        if (onClose) onClose(); else window.history.back();
    }, [background, elements, onSave, onClose]);
 
    const selectedElementData = useMemo(() => elements.find(e => e.id === selectedElement), [elements, selectedElement]);
 
    // ── Render ────────────────────────────────────────────────────────────────
    const isNarrow = isMobile || isTablet;
    const canvasScale = canvasWidth / 500;
 
    // All panel tab content — same markup regardless of breakpoint
    const panelContent = (
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '10px' : '14px', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
 


            {/* BACKGROUND */}
            {activeTab === 'background' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                        <SectionHeader label="🖼️ Themed Backgrounds" count={imageBackgrounds.length} open={showBgImages} onToggle={() => setShowBgImages(p => !p)} />
                        {showBgImages && (
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3,1fr)' : 'repeat(2,1fr)', gap: '8px', marginTop: '8px' }}>
                                {imageBackgrounds.map((bg, i) => (
                                    <button key={i} onClick={() => setBackground({ type: 'image', value: bg.url })}
                                        style={{ height: isMobile ? '60px' : '80px', backgroundImage: `url(${bg.url})`, backgroundSize: 'cover', backgroundPosition: 'center', border: background.type === 'image' && background.value === bg.url ? `3px solid ${C.primary}` : `1.5px solid ${C.outlineVariant}`, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '4px' }}>
                                        <span style={{ background: 'rgba(0,0,0,0.65)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '700' }}>{bg.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <SectionHeader label="🎨 Solid Colors" count={solidColors.length} open={showSolidColors} onToggle={() => setShowSolidColors(p => !p)} />
                        {showSolidColors && (
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill,minmax(${isMobile ? '36px' : '44px'},1fr))`, gap: '6px', marginTop: '8px' }}>
                                {solidColors.map(color => (
                                    <button key={color} onClick={() => setBackground({ type: 'solid', value: color })}
                                        style={{ height: isMobile ? '36px' : '44px', background: color, border: background.type === 'solid' && background.value === color ? `3px solid ${C.onBackground}` : `1.5px solid ${C.outlineVariant}`, borderRadius: '7px', cursor: 'pointer' }} />
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <SectionHeader label="🌈 Gradients" count={gradients.length} open={showGradients} onToggle={() => setShowGradients(p => !p)} />
                        {showGradients && (
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3,1fr)' : 'repeat(2,1fr)', gap: '8px', marginTop: '8px' }}>
                                {gradients.map((g, i) => (
                                    <button key={i} onClick={() => setBackground({ type: 'gradient', value: g })}
                                        style={{ height: isMobile ? '44px' : '56px', background: g, border: background.type === 'gradient' && background.value === g ? `3px solid ${C.onBackground}` : `1.5px solid ${C.outlineVariant}`, borderRadius: '8px', cursor: 'pointer' }} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
 
            {/* STICKERS */}
            {activeTab === 'stickers' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {(Object.keys(graphicsLibrary) as Array<keyof GraphicsLibrary>).map(cat => {
                        if (cat === 'custom') return null;
                        const list: Graphic[] = graphicsLibrary[cat];
                        if (!list.length) return null;
                        const open = expandedSections.has(cat);
                        return (
                            <div key={cat}>
                                <SectionHeader label={`🎨 ${cat}`} count={list.length} open={open} onToggle={() => toggleSection(cat)} />
                                {open && (
                                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill,minmax(${isMobile ? '60px' : '70px'},1fr))`, gap: '8px', marginTop: '8px' }}>
                                        {list.map(g => <GraphicButton key={g.id} graphic={g} onClick={addGraphicToCanvas} />)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
 
            {/* MY ART */}
            {activeTab === 'my-art' && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

        {/* ── Full cover upload ─────────────────────────────────── */}
        <div>
            <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '0.78rem', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                🖼️ Set as Full Cover
            </div>
            <label htmlFor="cover-upload"
                style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: isMobile ? '14px 12px' : '20px 16px', background: C.secondaryContainer, border: `2px dashed ${C.secondary}`, borderRadius: '12px', cursor: uploadingCover ? 'wait' : 'pointer', boxSizing: 'border-box' as const, transition: 'all 0.15s' }}>
                <span style={{ fontSize: isMobile ? '1.8rem' : '2.2rem' }}>🎨</span>
                <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: isMobile ? '0.82rem' : '0.9rem', color: C.secondary, textAlign: 'center' }}>
                    {uploadingCover ? 'Processing…' : 'Upload Full-Cover Art'}
                </span>
                <span style={{ fontSize: '0.7rem', color: C.onSurfaceVariant, textAlign: 'center' }}>
                    ProCreate · Canva · Any drawing app
                </span>
                <span style={{ fontSize: '0.65rem', color: C.outlineVariant, textAlign: 'center' }}>
                    PNG · JPG · up to 50 MB · auto-compressed
                </span>
                {!uploadingCover && (
                    <div style={{ padding: '6px 16px', background: C.secondary, color: '#fff', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', marginTop: '2px' }}>
                        Choose File
                    </div>
                )}
            </label>
            <input id="cover-upload" type="file" accept="image/*" onChange={handleCoverUpload} disabled={uploadingCover} style={{ display: 'none' }} />
            <p style={{ fontSize: '0.67rem', color: C.onSurfaceVariant, textAlign: 'center', margin: '6px 0 0' }}>
                Fills the entire 5×8 cover — existing background is replaced
            </p>
        </div>

        <div style={{ height: '1px', background: C.surfaceHigh }} />

        {/* ── Sticker / art upload ──────────────────────────────── */}
        <div>
            <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '0.78rem', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                ✂️ Add as Sticker
            </div>
            <label htmlFor="graphic-upload"
                style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: isMobile ? '12px' : '18px 16px', background: C.primaryContainer, border: `2px dashed ${C.primary}`, borderRadius: '12px', cursor: uploadingGraphic ? 'wait' : 'pointer', boxSizing: 'border-box' as const, transition: 'all 0.15s' }}>
                <span style={{ fontSize: isMobile ? '1.8rem' : '2.2rem' }}>✏️</span>
                <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: isMobile ? '0.82rem' : '0.9rem', color: C.primary, textAlign: 'center' }}>
                    {uploadingGraphic ? 'Processing…' : 'Upload a Drawing'}
                </span>
                <span style={{ fontSize: '0.7rem', color: C.onSurfaceVariant, textAlign: 'center' }}>
                    Draw on paper, take a photo, add here!
                </span>
                <span style={{ fontSize: '0.65rem', color: C.outlineVariant }}>PNG · JPG · GIF · up to 15 MB</span>
                {!uploadingGraphic && (
                    <div style={{ padding: '6px 16px', background: C.primary, color: '#fff', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', marginTop: '2px' }}>
                        Choose File
                    </div>
                )}
            </label>
            <input id="graphic-upload" type="file" accept="image/*" onChange={handleGraphicUpload} disabled={uploadingGraphic} style={{ display: 'none' }} />
        </div>

        {customGraphics.length > 0 && (
            <>
                <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.82rem', color: C.onBackground }}>
                    ✨ Your Uploaded Art ({customGraphics.length})
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill,minmax(${isMobile ? '60px' : '70px'},1fr))`, gap: '8px' }}>
                    {customGraphics.map(g => <GraphicButton key={g.id} graphic={g} onClick={addGraphicToCanvas} onDelete={deleteCustomGraphic} />)}
                </div>
            </>
        )}

        {customGraphics.length === 0 && (
            <div style={{ textAlign: 'center', padding: '16px', background: C.surfaceLow, borderRadius: '10px', color: C.onSurfaceVariant, fontSize: '0.82rem' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>🖼️</div>
                No sticker art yet — upload your first drawing!
            </div>
        )}
    </div>
)}
 
            {/* TEXT */}
            {activeTab === 'text' && (
                <div>
                    {selectedElementData?.type === 'text' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '14px' : '18px' }}>
                            <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Edit Text</div>
                                <input type="text" value={selectedElementData.content || ''} onChange={e => updateElement(selectedElement!, { content: e.target.value })}
                                    style={{ width: '100%', padding: '10px 12px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '10px', fontSize: '0.9rem', fontWeight: '600', boxSizing: 'border-box' as const, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                    onFocus={e => { e.target.style.borderColor = C.primary; }} onBlur={e => { e.target.style.borderColor = C.outlineVariant; }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Color</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '3px', overflow: 'hidden' }}>
    {textColors.map(color => (
        <button key={color.value} onClick={() => updateElement(selectedElement!, { color: color.value }, true)} title={color.name}
            style={{ height: '22px', width: '100%', background: color.value, border: selectedElementData.color === color.value ? `3px solid ${C.onBackground}` : `1.5px solid ${C.outlineVariant}`, borderRadius: '4px', cursor: 'pointer', minWidth: 0, padding: 0, boxSizing: 'border-box' as const }} />
    ))}
</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Font</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px' }}>
                                    {fonts.map(f => (
                                        <button key={f.value} onClick={() => updateElement(selectedElement!, { fontFamily: f.value })}
                                            style={{ padding: '8px 6px', minHeight: '40px', background: selectedElementData.fontFamily === f.value ? C.primary : C.surfaceLowest, color: selectedElementData.fontFamily === f.value ? '#fff' : C.onSurface, border: `1.5px solid ${selectedElementData.fontFamily === f.value ? C.primary : C.outlineVariant}`, borderRadius: '8px', cursor: 'pointer', fontSize: isMobile ? '0.78rem' : '0.85rem', fontWeight: '600', fontFamily: f.value }}>
                                            {f.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Style</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px' }}>
        {([
            { label: '💧 Shadow',  value: 'shadow'  },
            { label: '⬜ Outline', value: 'outline' },
            { label: '✨ Glow',   value: 'glow'    },
            { label: '∅ None',    value: 'none'    },
        ] as const).map(s => (
            <button key={s.value} onClick={() => updateElement(selectedElement!, { textStyle: s.value }, true)}
                style={{ padding: '7px 4px', fontSize: '0.7rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", background: selectedElementData?.textStyle === s.value ? C.primary : C.surfaceLowest, color: selectedElementData?.textStyle === s.value ? '#fff' : C.onSurface, border: `1.5px solid ${selectedElementData?.textStyle === s.value ? C.primary : C.outlineVariant}`, borderRadius: '8px', cursor: 'pointer' }}>
                {s.label}
            </button>
        ))}
    </div>
</div>
<div>
    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Curve</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px' }}>
        {([
            { label: '— Straight', value: 'none' },
            { label: '⌢ Arch Up',  value: 'up'   },
            { label: '⌣ Arch Down',value: 'down'  },
        ] as const).map(c => (
            <button key={c.value} onClick={() => updateElement(selectedElement!, { textCurve: c.value }, true)}
                style={{ padding: '7px 4px', fontSize: '0.7rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", background: (selectedElementData?.textCurve ?? 'none') === c.value ? C.primary : C.surfaceLowest, color: (selectedElementData?.textCurve ?? 'none') === c.value ? '#fff' : C.onSurface, border: `1.5px solid ${(selectedElementData?.textCurve ?? 'none') === c.value ? C.primary : C.outlineVariant}`, borderRadius: '8px', cursor: 'pointer' }}>
                {c.label}
            </button>
        ))}
    </div>
</div>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px' }}>Size</span>
                                    <span style={{ fontSize: '0.82rem', color: C.primary, fontWeight: '700' }}>{selectedElementData.fontSize || 16}px</span>
                                </div>
                                <input type="range" min="12" max="72"
    value={selectedElementData.fontSize || 16}
    onChange={e => updateElement(selectedElement!, { fontSize: parseInt(e.target.value) })}
    onPointerUp={() => pushHistory()}
    style={{ width: '100%', accentColor: C.primary }} />
                            </div>
                            <button onClick={() => { deleteElement(selectedElement!); setSelectedElement(null); }}
                                style={{ padding: '10px', background: C.errorContainer, color: C.error, border: 'none', borderRadius: '9999px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                🗑️ Delete Text
                            </button>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: isMobile ? '20px 12px' : '32px 16px', background: C.surfaceLow, borderRadius: '12px' }}>
                            <div style={{ fontSize: isMobile ? '2rem' : '2.5rem', marginBottom: '10px' }}>✏️</div>
                            <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: isMobile ? '0.85rem' : '0.95rem', color: C.onBackground, marginBottom: '6px' }}>Tap text on your cover to edit it</div>
                            <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, marginBottom: '16px' }}>Or add a new text element</div>
                            <button
                                onClick={() => { const el: CoverElement = { id: `text-${Date.now()}`, type: 'text', content: 'New Text', x: 50, y: 50, fontSize: 24, fontFamily: 'Fredoka', color: '#FFFFFF' }; setElements(p => [...p, el]); setSelectedElement(el.id); }}
                                style={{ padding: '10px 22px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}>
                                + Add Text
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
 
    // Shared tab bar
    const tabBar = (compact: boolean) => (
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.surfaceHigh}`, flexShrink: 0, background: C.surfaceLow }}>
            {tabConfig.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                    style={{ flex: 1, padding: compact ? '8px 4px' : '12px 8px', background: 'none', border: 'none', borderBottom: `3px solid ${activeTab === tab.id ? C.primary : 'transparent'}`, color: activeTab === tab.id ? C.primary : C.onSurfaceVariant, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', transition: 'all 0.15s' }}>
                    <span style={{ fontSize: compact ? '1.1rem' : '1.3rem' }}>{tab.icon}</span>
                    <span style={{ fontSize: compact ? '0.58rem' : '0.62rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif" }}>{tab.label}</span>
                </button>
            ))}
        </div>
    );
 
    // Shared canvas
// NEW

console.log(canvasWidth, canvasScale)
const canvasEl = (
  <div
      ref={canvasOuterRef}
      onClick={handleCanvasClick}
      onMouseMove={handleInteractionMove}
      onMouseUp={handleInteractionEnd}
      onMouseLeave={handleInteractionEnd}
      onTouchMove={handleInteractionMove}
      onTouchEnd={handleInteractionEnd}
      style={{
          height: '100%',
          width: 'auto',
          aspectRatio: '5/8',
          borderRadius: isNarrow ? '10px' : '12px',
          position: 'relative',
          overflow: 'visible',  // keep visible — floating toolbar overflows
          boxShadow: isNarrow
              ? '0 8px 28px rgba(0,0,0,0.22)'
              : '0 20px 60px rgba(0,0,0,0.25), 0 6px 16px rgba(0,0,0,0.12)',
          cursor: dragging ? 'grabbing' : 'default',
          border: background.type === 'solid' && background.value === '#FFFFFF' ? `2px solid ${C.surfaceHigh}` : 'none',
          flexShrink: 0,
          ...getBackgroundStyle(),  // background still on outer — fills responsive size correctly
      }}
  >
      {/* Fixed 500×800 inner canvas scaled to fit the outer div.
          All element coordinates (x%, y%, fontSize, maxWidth) are now
          relative to this base — identical to CoverRenderer. */}
      <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 500,
          height: 800,
          transformOrigin: 'top left',
          transform: `scale(${canvasScale})`,
          overflow: 'visible',
      }}>
          {elements.map(el => (
              <CanvasElement key={el.id} element={el}
                  isSelected={selectedElement === el.id}
                  isDragging={dragging === el.id}
                  onInteractionStart={handleInteractionStart}
                  onUpdate={updateElement} onDelete={deleteElement}
                  onSelect={setSelectedElement} setActiveTab={setActiveTab} />
          ))}
      </div>
      {/* Snap guide lines */}
{snapLines.x !== undefined && (
    <div style={{ position: 'absolute', left: `${snapLines.x}%`, top: 0, bottom: 0, width: '1px', background: `${C.primary}88`, pointerEvents: 'none', zIndex: 999 }} />
)}
{snapLines.y !== undefined && (
    <div style={{ position: 'absolute', top: `${snapLines.y}%`, left: 0, right: 0, height: '1px', background: `${C.primary}88`, pointerEvents: 'none', zIndex: 999 }} />
)}
  </div>
);
 
    return (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', fontFamily: "'Plus Jakarta Sans', sans-serif", height: '100dvh', maxHeight: '100dvh', overflow: 'hidden', background: C.surface }}>
 
            {/* ── Top bar ───────────────────────────────────────────────────── */}
            <div style={{ height: isMobile ? 50 : 60, background: C.surfaceLowest, borderBottom: `1px solid ${C.surfaceHigh}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 12px' : '0 28px', flexShrink: 0, zIndex: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.2rem' }}>🎨</span>
                    {!isMobile && <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1rem', color: C.onBackground }}>Cover Designer</span>}
                    <span style={{ background: C.tertiaryContainer, color: C.tertiary, padding: '2px 7px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800' }}>5×8</span>
                </div>

                {/* Undo / Redo */}
<div style={{ display: 'flex', gap: '4px' }}>
    {[
        { fn: undo, icon: '↩︎', title: 'Undo (Ctrl+Z)', enabled: canUndo },
        { fn: redo, icon: '↪︎', title: 'Redo (Ctrl+Y)', enabled: canRedo },
    ].map(({ fn, icon, title, enabled }) => (
        <button key={icon} onClick={fn} title={title} disabled={!enabled}
            style={{ padding: '6px 12px', background: enabled ? C.surfaceLow : 'transparent', border: `1.5px solid ${enabled ? C.outlineVariant : 'transparent'}`, borderRadius: '8px', fontSize: '1rem', cursor: enabled ? 'pointer' : 'default', color: enabled ? C.onSurface : C.outlineVariant, transition: 'all 0.15s' }}>
            {icon}
        </button>
    ))}
</div>

                <button onClick={handleClose}
                    style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', borderRadius: '9999px', padding: isMobile ? '7px 16px' : '9px 22px', fontSize: isMobile ? '0.78rem' : '0.85rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 12px ${C.primary}40`, whiteSpace: 'nowrap' }}>
                    {isMobile ? 'Done ✓' : 'Return to Writing →'}
                </button>
            </div>
 
            {/* ── MOBILE: canvas top (fixed height) + panel below (scrolls) ─── */}
            {isMobile && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    {/* Canvas — hard 42vh, never shifts */}
                    <div style={{ flexShrink: 0, height: '42vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.surface, padding: '8px 16px 6px' }}>
                        <div style={{ fontSize: '0.6rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                            📖 Your Cover · 5×8
                        </div>
                        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {canvasEl}
                        </div>
                        <div style={{ fontSize: '0.6rem', color: C.onSurfaceVariant, marginTop: '4px' }}>
                            Tap to select · Drag to move
                        </div>
                    </div>
                    {/* Panel — fills rest, tab bar sticky, content scrolls */}
                    <div style={{ flex: 1, minHeight: 0, background: C.surfaceLowest, borderTop: `2px solid ${C.surfaceHigh}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {tabBar(true)}
                        {panelContent}
                    </div>
                </div>
            )}
 
            {/* ── TABLET: canvas left (60%), panel right (40%) ──────────────── */}
            {isTablet && (
                <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
                    {/* Canvas — left 60%, fixed, never shifts */}
                    <div style={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.surface, padding: '20px', overflow: 'hidden' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                            📖 Your Book Cover · 5×8
                        </div>
                        <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {canvasEl}
                        </div>
                    </div>
                    {/* Panel — right 40%, tab bar sticky, content scrolls */}
                    <div style={{ flex: '0 0 40%', minWidth: 0, background: C.surfaceLowest, borderLeft: `1px solid ${C.surfaceHigh}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {tabBar(false)}
                        {panelContent}
                    </div>
                </div>
            )}
 
            {/* ── DESKTOP: 300px panel left, canvas fills right — CSS Grid ──── */}
            {!isNarrow && (
                <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gridTemplateRows: '1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {/* Canvas — column 2, pinned */}
                    <div style={{ gridColumn: '2', gridRow: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 48px', background: C.surface, overflow: 'hidden', minHeight: 0 }}>
                        <div style={{ marginBottom: '10px', fontSize: '0.72rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            <span>📖 Your Book Cover</span>
                            <span style={{ background: C.primaryContainer, color: C.primary, padding: '2px 8px', borderRadius: '9999px', fontSize: '0.6rem' }}>5 × 8 inches</span>
                        </div>
                        <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {canvasEl}
                        </div>
                    </div>
                    {/* Panel — column 1, independent */}
                    <div style={{ gridColumn: '1', gridRow: '1', background: C.surfaceLowest, borderRight: `1px solid ${C.surfaceHigh}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {tabBar(false)}
                        {panelContent}
                    </div>
                </div>
            )}
 
        </div>
 
        <SignupPrompt isOpen={showSignupPrompt} onClose={() => setShowSignupPrompt(false)} reason="save" />
        <Notification isOpen={notification.show} onClose={() => setNotification({ ...notification, show: false })} type={notification.type} />
        </>
    );
};
 
export default CoverDesigner;